import _ from 'lodash';
import type { z } from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType, type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { mergeMissedMarks } from './utils';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBLegacyExamAttemptSchema, LegacyExamAttemptSchema } from './schemas';

type DBLegacyExamAttempt = z.infer<typeof DBLegacyExamAttemptSchema>;
export const LegacyExamAttemptCodecConfig: TransformerConfig<
  typeof LegacyExamAttemptSchema,
  typeof DBLegacyExamAttemptSchema
> = {
  name: 'LegacyExamAttempt',
  clientSchema: LegacyExamAttemptSchema,
  dbSchema: DBLegacyExamAttemptSchema,
  transformers: [
    // examSubject, examId, attemptNumber ↔ subjectExamQuestionIdentifier, exam
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      encoder: {
        sourceFields: ['examSubject', 'examId', 'attemptNumber'] as const,
        encode: ({ examSubject, examId, attemptNumber }) => ({
          exam: `${examSubject}#${examId}`,
          subjectExamQuestionIdentifier: `${LegacyExamAttemptType.LegacyExam}#${examSubject}#${examId}#${attemptNumber}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectExamQuestionIdentifier', 'exam'] as const,
        decode: ({ subjectExamQuestionIdentifier }) => {
          const identifier =
            subjectExamQuestionIdentifier as DBLegacyExamAttempt['subjectExamQuestionIdentifier'];
          const parts = identifier.split('#');
          return {
            examSubject: parts[1] as Subject,
            examId: parts[2] as string,
            attemptNumber: _.toInteger(parts[3]),
          };
        },
      },
    }),
    // status: LegacyExamAttemptStatus ↔ status: 'EXAM#status'
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      encoder: {
        sourceFields: ['status'] as const,
        encode: ({ status }) => ({
          status: `${LegacyExamAttemptType.LegacyExam}#${status}`,
        }),
      },
      decoder: {
        sourceFields: ['status'] as const,
        decode: ({ status }) => ({
          status: (status as DBLegacyExamAttempt['status']).split(
            '#'
          )[1] as LegacyExamAttemptStatus,
        }),
      },
    }),
    // Merge missedMarks into inaccuracyMarks for each analytics sub-object on decode
    createTransformer<typeof LegacyExamAttemptSchema, typeof DBLegacyExamAttemptSchema>({
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExamAttempt['analytics'];
          return {
            analytics: Object.fromEntries(
              Object.entries(dbAnalytics).map(([key, value]) => [key, mergeMissedMarks(value)])
            ),
          };
        },
      },
    }),
  ],
};

export const encodeLegacyExamAttemptToDb = createEncodeToDbFn(LegacyExamAttemptCodecConfig);
export const decodeLegacyExamAttemptFromDb = createDecodeFromDbFn(LegacyExamAttemptCodecConfig);
